import { signal, computed } from '@preact/signals';
import { useEffect } from 'preact/hooks';

// ─── constants ───────────────────────────────────────────────────────────────

const DISMISS_KEY = '_id0';
const DISMISS_TTL_DAYS = 30;

// ─── types ────────────────────────────────────────────────────────────────────

type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type OS = 'ios' | 'android' | 'other';
type Browser = 'safari' | 'chrome' | 'samsung' | 'firefox' | 'other';

// ─── platform detection ───────────────────────────────────────────────────────

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  return (window.navigator as { standalone?: boolean }).standalone === true;
}

function getOS(): OS {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1);
  if (isIOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

function getBrowser(): Browser {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  // Order matters — Samsung before Chrome (Samsung UA includes "Chrome")
  if (/SamsungBrowser/.test(ua)) return 'samsung';
  // iOS browsers other than Safari can't install PWAs
  if (/CriOS|FxiOS|EdgiOS/.test(ua)) return 'other';
  if (/Chrome/.test(ua)) return 'chrome';
  if (/Firefox/.test(ua)) return 'firefox';
  if (/Safari/.test(ua)) return 'safari';
  return 'other';
}

// ─── signals ─────────────────────────────────────────────────────────────────

const deferred = signal<BIPEvent | null>(null);
const engaged = signal(false);
const dismissed = signal(readDismissed());
const overlayOpen = signal(false);

const installPromptVisible = computed(() => {
  if (typeof window === 'undefined') return false;
  if (isStandalone()) return false;
  if (dismissed.value) return false;
  if (!engaged.value) return false;

  const os = getOS();
  const browser = getBrowser();

  // iOS: only Safari supports PWA install
  if (os === 'ios') return browser === 'safari';

  // Android: show if native prompt ready OR on Chrome/Samsung (can guide manually)
  if (os === 'android') {
    return deferred.value !== null || browser === 'chrome' || browser === 'samsung';
  }

  // Desktop: only if browser gave us a native prompt
  return deferred.value !== null;
});

// ─── helpers ─────────────────────────────────────────────────────────────────

function readDismissed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const age = (Date.now() - Number(v)) / 86_400_000;
    return age < DISMISS_TTL_DAYS;
  } catch {
    return false;
  }
}

function dismiss(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  dismissed.value = true;
}

/** Call when the user has done something meaningful (scroll, audio, chapter nav). */
export function markEngaged(): void {
  engaged.value = true;
}

// ─── main component ──────────────────────────────────────────────────────────

export function InstallPrompt() {
  useEffect(() => {
    const onBIP = (e: Event): void => {
      e.preventDefault();
      deferred.value = e as BIPEvent;
    };
    const onInstalled = (): void => {
      deferred.value = null;
      dismiss();
    };
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!installPromptVisible.value) return null;

  const os = getOS();
  const browser = getBrowser();

  const onInstall = async (): Promise<void> => {
    const ev = deferred.value;
    if (ev) {
      // Android Chrome gave us the native install prompt — use it
      await ev.prompt();
      const result = await ev.userChoice;
      deferred.value = null;
      if (result.outcome === 'dismissed') dismiss();
      return;
    }
    // iOS Safari or Android without a native prompt — show manual guide
    overlayOpen.value = true;
  };

  return (
    <>
      <div class="pwa-banner" role="complementary" aria-label="Add to home screen">
        <div class="pwa-banner-icon" aria-hidden="true">
          {/* open-book glyph */}
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path d="M4 4h7a4 4 0 0 1 4 4v12a3 3 0 0 0-3-3H4V4z"/>
            <path d="M20 4h-7a4 4 0 0 0-4 4v12a3 3 0 0 1 3-3h8V4z"/>
          </svg>
        </div>
        <div class="pwa-banner-text">
          <div class="pwa-banner-title">Save to home screen</div>
          <div class="pwa-banner-sub">One-tap access, works offline.</div>
        </div>
        <button type="button" class="pwa-banner-add" onClick={() => void onInstall()}>
          Add
        </button>
        <button
          type="button"
          class="pwa-banner-dismiss"
          aria-label="Dismiss"
          onClick={dismiss}
        >
          <svg width="14" height="14" viewBox="0 0 24 24">
            <path d="M6 6l12 12M18 6L6 18"/>
          </svg>
        </button>
      </div>
      {overlayOpen.value ? (
        <InstallGuide
          os={os}
          browser={browser}
          onClose={() => (overlayOpen.value = false)}
        />
      ) : null}
    </>
  );
}

// ─── platform-specific guides ────────────────────────────────────────────────

function InstallGuide({
  os,
  browser,
  onClose,
}: {
  os: OS;
  browser: Browser;
  onClose: () => void;
}) {
  if (os === 'ios') return <IosGuide onClose={onClose} />;
  if (browser === 'samsung') return <SamsungGuide onClose={onClose} />;
  return <AndroidChromeGuide onClose={onClose} />;
}

// iOS Share icon — box with upward arrow (matches Safari's actual button)
function IosShareIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <polyline points="8 6 12 2 16 6" />
      <line x1="12" y1="2" x2="12" y2="16" />
      <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6" />
    </svg>
  );
}

function IosGuide({ onClose }: { onClose: () => void }) {
  return (
    <div
      class="install-guide-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="How to add to home screen"
    >
      <div class="install-guide-card" onClick={(e) => e.stopPropagation()}>
        <div class="install-guide-header">
          <div class="install-guide-platform-badge">
            <IosShareIcon size={22} />
          </div>
          <h2 class="install-guide-title">Add to Home Screen</h2>
          <button type="button" class="install-guide-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <ol class="install-guide-steps">
          <li>
            <span class="install-guide-step-num">1</span>
            <span class="install-guide-step-text">
              Tap the{' '}
              <span class="install-guide-inline-icon" aria-label="Share button">
                <IosShareIcon size={15} />
              </span>{' '}
              <strong>Share</strong> button in Safari's toolbar at the bottom of your screen
            </span>
          </li>
          <li>
            <span class="install-guide-step-num">2</span>
            <span class="install-guide-step-text">
              Scroll down in the menu and tap{' '}
              <strong>Add to Home Screen</strong>
            </span>
          </li>
          <li>
            <span class="install-guide-step-num">3</span>
            <span class="install-guide-step-text">
              Tap <strong>Add</strong> in the top-right corner to confirm
            </span>
          </li>
        </ol>

        {/* Arrow pointing down toward Safari's bottom toolbar */}
        <div class="install-guide-arrow-hint install-guide-arrow-hint--down">
          <svg width="20" height="28" viewBox="0 0 20 28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="10" y1="2" x2="10" y2="22" />
            <polyline points="4 16 10 24 16 16" />
          </svg>
          <span>Safari toolbar is below</span>
        </div>

        <button type="button" class="install-guide-ok" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );
}

function AndroidChromeGuide({ onClose }: { onClose: () => void }) {
  return (
    <div
      class="install-guide-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="How to add to home screen"
    >
      <div class="install-guide-card" onClick={(e) => e.stopPropagation()}>
        <div class="install-guide-header">
          <div class="install-guide-platform-badge">
            {/* Chrome three-dot menu icon */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="12" cy="5" r="1.8" />
              <circle cx="12" cy="12" r="1.8" />
              <circle cx="12" cy="19" r="1.8" />
            </svg>
          </div>
          <h2 class="install-guide-title">Add to Home Screen</h2>
          <button type="button" class="install-guide-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {/* Arrow pointing up toward Chrome's top-right menu */}
        <div class="install-guide-arrow-hint install-guide-arrow-hint--up">
          <svg width="20" height="28" viewBox="0 0 20 28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="10" y1="26" x2="10" y2="6" />
            <polyline points="4 12 10 4 16 12" />
          </svg>
          <span>Chrome's ⋮ menu is above</span>
        </div>

        <ol class="install-guide-steps">
          <li>
            <span class="install-guide-step-num">1</span>
            <span class="install-guide-step-text">
              Tap <strong>⋮</strong> in Chrome's top-right corner
            </span>
          </li>
          <li>
            <span class="install-guide-step-num">2</span>
            <span class="install-guide-step-text">
              Tap <strong>Add to Home Screen</strong>
            </span>
          </li>
          <li>
            <span class="install-guide-step-num">3</span>
            <span class="install-guide-step-text">
              Tap <strong>Add</strong> to confirm
            </span>
          </li>
        </ol>

        <button type="button" class="install-guide-ok" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );
}

function SamsungGuide({ onClose }: { onClose: () => void }) {
  return (
    <div
      class="install-guide-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="How to add to home screen"
    >
      <div class="install-guide-card" onClick={(e) => e.stopPropagation()}>
        <div class="install-guide-header">
          <h2 class="install-guide-title">Add to Home Screen</h2>
          <button type="button" class="install-guide-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <ol class="install-guide-steps">
          <li>
            <span class="install-guide-step-num">1</span>
            <span class="install-guide-step-text">
              Tap <strong>☰</strong> in Samsung Internet's menu bar
            </span>
          </li>
          <li>
            <span class="install-guide-step-num">2</span>
            <span class="install-guide-step-text">
              Tap <strong>Add page to</strong>
            </span>
          </li>
          <li>
            <span class="install-guide-step-num">3</span>
            <span class="install-guide-step-text">
              Tap <strong>Home Screen</strong>
            </span>
          </li>
        </ol>

        <button type="button" class="install-guide-ok" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );
}
